import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  ApiError,
  SdkSendEmailHandle,
  SdkSendEmailInput,
  SdkSendEmailResponse,
  SdkUseSendEmailOptions
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export const SEND_EMAIL_URL = '/api/email-messages';

/**
 * Reactive wrapper around `POST /api/email-messages` for pages that send an ad hoc
 * email from code — a "Resend invoice" button, a follow-up after a form submit. The
 * payload is posted as-is, so it mirrors the endpoint's request shape exactly. A
 * queued email refreshes every `EmailMessages` list on the page.
 *
 * Exposed via `Sdk.hooks.useSendEmail(options)`.
 */
export function useSendEmail(options?: SdkUseSendEmailOptions): SdkSendEmailHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<SdkSendEmailResponse, ApiError, SdkSendEmailInput>({
    mutationFn: (input) =>
      axios.post<SdkSendEmailResponse>(SEND_EMAIL_URL, input).then(
        (response) => response.data,
        (error: unknown) => {
          throw toApiError(error);
        }
      ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['email-messages', 'search'] });

      options?.onSuccess?.(response);
    },
    onError: (error) => options?.onError?.(error)
  });

  return {
    send: (input) =>
      mutation.mutateAsync(input).then(
        (response) => response ?? null,
        () => null
      ),
    data: mutation.data,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: () => {
      mutation.reset();
    }
  };
}
